import {
  memo,
  useRef,
  useState,
  useEffect,
  ChangeEvent,
  KeyboardEvent,
  ReactNode,
} from "react";

import ChevronDown from "@zenith/assets/icons/ChevronDown";
import SearchIcon from "@zenith/assets/icons/SearchIcon";
import Check from "@zenith/assets/icons/Check";
import Close from "@zenith/assets/icons/Close";
import SpinnerComponent from "@zenith/components/ui/SpinnerComponent/spinner-component/spinner-component";

export interface DropdownOption {
  value: string;
  label: string;
  /** First display line; falls back to `label` when omitted. */
  primary?: string;
  /** Optional second display line (e.g. the city under a street). */
  secondary?: string;
  disabled?: boolean;
}

interface AutocompleteComponentProps {
  id: string;
  label: string;
  labelId?: string;
  options: DropdownOption[];
  placeholder?: string;
  selectValue?: DropdownOption | null;
  onChange: (value: string, label: string) => void;
  /** When true, the trigger input doubles as a search/filter field */
  showSearchIcon?: boolean;
  value?: string;
  onInputChange?: (value: string) => void;
  disabled?: boolean;
  isLoading?: boolean;
  hasError?: boolean;
  errorMessage?: string;
  className?: string;
  labelClassName?: string;
  noOptionsText?: string;
  noOptionsTitle?: string;
  noOptionsSubtitle?: string;
  noOptionsIcon?: ReactNode;
  maxLength?: number;
  onBlur?: (event: React.FocusEvent) => void;
  allowEdit?: boolean;
  trailingLoading?: boolean;
  /** False when the options come from a search endpoint that already ranked them. */
  filterOptions?: boolean;
  /** True to grow the field instead of floating the list over the page. */
  inlineList?: boolean;
}

const AutocompleteComponent = memo(function AutocompleteComponent(
  props: AutocompleteComponentProps
): JSX.Element {
  const {
    id,
    label,
    labelId,
    options,
    placeholder = "Select",
    selectValue,
    onChange,
    showSearchIcon = false,
    value: controlledSearch,
    onInputChange,
    disabled = false,
    isLoading = false,
    hasError = false,
    errorMessage,
    className,
    labelClassName,
    noOptionsText = "No Options Available",
    noOptionsTitle,
    noOptionsSubtitle,
    noOptionsIcon,
    maxLength,
    onBlur,
    allowEdit = false,
    trailingLoading = false,
    filterOptions = true,
    inlineList = false,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const comboInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const searchValue = controlledSearch ?? internalSearch;

  /**
   * Narrow by every word the user typed rather than by the whole string: an
   * exact-substring test threw results away ("Ikeja Lagos Nigeria" discarded
   * "Ikeja Bridge, Lagos, Nigeria"). This is only a fallback — a field whose
   * options come from a search endpoint passes filterOptions={false}, because
   * even the token test drops the server's fuzzy hits: "Lekki Phase 1, Lagos"
   * carries the token "1," (matching nothing), and without the comma "1"
   * still cannot match Google's "Phase I".
   */
  const searchTokens = searchValue
    ? searchValue.toLowerCase().split(/\s+/).filter(Boolean)
    : [];
  const filteredOptions =
    filterOptions && showSearchIcon && searchTokens.length
      ? options.filter((opt) => {
          const haystack = opt.label.toLowerCase();
          return searchTokens.every((token) => haystack.includes(token));
        })
      : options;

  // Close on outside click
  useEffect(() => {
    function handleOutsideClick(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
        setInternalSearch("");
        setFocusedIndex(-1);
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setInternalSearch("");
      setFocusedIndex(-1);
    } else if (selectValue) {
      if (allowEdit) {
        setInternalSearch(selectValue.label);
      }
      const idx = filteredOptions.findIndex(
        (o) => o.value === selectValue.value
      );
      if (idx >= 0) setFocusedIndex(idx);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const el = listRef.current.children[focusedIndex] as HTMLElement;
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex]);

  const closeDropdown = () => {
    setIsOpen(false);
    triggerRef.current?.focus();
    comboInputRef.current?.blur();
  };

  const handleToggle = () => {
    if (!disabled) setIsOpen((prev) => !prev);
  };

  const handleSelect = (option: DropdownOption) => {
    if (option.disabled) return;
    onChange(option.value, option.label);
    closeDropdown();
  };

  // Combobox: typing in the trigger input filters options
  const handleComboChange = (e: ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInternalSearch(val);
    onInputChange?.(val);
    setFocusedIndex(-1);
    if (!isOpen) setIsOpen(true);
  };

  const handleComboFocus = () => {
    if (!disabled && !isOpen) setIsOpen(true);
  };

  const handleClearSearch = () => {
    setInternalSearch("");
    onInputChange?.("");
    setFocusedIndex(-1);
    comboInputRef.current?.focus();
  };

  const handleComboKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) setIsOpen(true);
        else
          setFocusedIndex((p) => Math.min(p + 1, filteredOptions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((p) => Math.max(p - 1, 0));
        break;
      case "Enter":
        if (focusedIndex >= 0 && filteredOptions[focusedIndex])
          handleSelect(filteredOptions[focusedIndex]);
        break;
      case "Escape":
        closeDropdown();
        break;
    }
  };

  const handleButtonKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        handleToggle();
        break;
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) setIsOpen(true);
        else
          setFocusedIndex((p) => Math.min(p + 1, filteredOptions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((p) => Math.max(p - 1, 0));
        break;
      case "Escape":
        closeDropdown();
        break;
    }
  };

  const handleListKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((p) => Math.min(p + 1, filteredOptions.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((p) => Math.max(p - 1, 0));
        break;
      case "Enter":
        if (focusedIndex >= 0 && filteredOptions[focusedIndex])
          handleSelect(filteredOptions[focusedIndex]);
        break;
      case "Escape":
        closeDropdown();
        break;
    }
  };

  const isOptionSelected = (o: DropdownOption) =>
    selectValue?.value === o.value;

  // Fix 3: red border ONLY when hasError — no special colour on open/focus
  const borderClass = (() => {
    if (disabled) return "border-neutral-200";
    if (hasError) return "border-red-400";
    return "border-neutral-300 hover:border-neutral-400";
  })();

  // Fix 2: match dropdown border to trigger border colour
  const dropdownBorderClass = hasError
    ? "border-red-400"
    : "border-neutral-300";

  // An editable combobox with no selection to show (e.g. the address search,
  // which is driven purely by `value`) must display the text it was handed.
  // Falling back to "" blanked the field as soon as the dropdown closed, so a
  // picked suggestion never appeared in the input.
  const comboValue = selectValue && !isOpen ? selectValue.label : searchValue;
  const comboPlaceholder =
    isOpen && selectValue ? selectValue.label : placeholder;

  return (
    <div ref={containerRef} className={`relative w-full ${className ?? ""}`}>
      {/* Label */}
      <label
        id={labelId}
        htmlFor={id}
        className={`mb-1.5 block text-xs font-medium text-neutral-600 ${
          labelClassName ?? ""
        }`}
      >
        {label}
      </label>

      {/* Trigger — Fix 2: rounded-b-none when open so it merges with dropdown */}
      <div
        className={[
          "flex w-full items-center justify-between border px-3 py-3",
          "transition-colors duration-150",
          isOpen ? "rounded-t-lg rounded-b-none" : "rounded-lg",
          disabled ? "bg-neutral-100" : "bg-white",
          borderClass,
        ].join(" ")}
      >
        {showSearchIcon ? (
          // Fix 1: search icon on the left; Fix: combobox — trigger IS the search input
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {searchValue && (
              <SearchIcon className="h-4 w-4 shrink-0 text-neutral-400" />
            )}

            <input
              ref={comboInputRef}
              id={id}
              type="text"
              disabled={disabled}
              value={comboValue}
              placeholder={comboPlaceholder}
              onChange={handleComboChange}
              onFocus={handleComboFocus}
              onKeyDown={handleComboKeyDown}
              onBlur={onBlur}
              maxLength={maxLength}
              autoComplete="off"
              className={[
                "text-md min-w-0 flex-1 bg-transparent outline-none",
                disabled
                  ? "cursor-not-allowed text-neutral-400"
                  : "cursor-text text-neutral-900",
                !selectValue && !isOpen
                  ? "placeholder:text-neutral-200"
                  : "placeholder:text-neutral-200",
              ].join(" ")}
            />
          </div>
        ) : (
          // Standard mode: button trigger, no search
          <button
            ref={triggerRef}
            id={id}
            type="button"
            disabled={disabled}
            onClick={handleToggle}
            onKeyDown={handleButtonKeyDown}
            onBlur={onBlur}
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            className={[
              "w-full text-left text-sm outline-none",
              disabled ? "cursor-not-allowed" : "cursor-pointer",
              selectValue ? "text-neutral-900" : "text-[#C6C6C6]",
            ].join(" ")}
          >
            {selectValue?.label ?? placeholder}
          </button>
        )}

        {/* Trailing spinner (e.g. an external lookup tied to this selection) */}
        {trailingLoading && (
          <span className="ml-2 shrink-0">
            <SpinnerComponent size={16} type="primary" showIcon={false} />
          </span>
        )}

        {/* Chevron / Clear */}
        {showSearchIcon && searchValue ? (
          <button
            type="button"
            disabled={disabled}
            onClick={handleClearSearch}
            tabIndex={-1}
            className="ml-2 shrink-0 outline-none"
          >
            <Close className="h-4 w-4 text-neutral-400" />
          </button>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={handleToggle}
            tabIndex={-1}
            className="ml-2 shrink-0 outline-none"
          >
            <ChevronDown
              className={[
                "h-4 w-4 transition-transform duration-200",
                isOpen ? "rotate-180" : "rotate-0",
                disabled ? "text-neutral-300" : "text-neutral-500",
              ].join(" ")}
            />
          </button>
        )}
      </div>

      {/* Error */}
      {hasError && errorMessage && (
        <p className="mt-1 text-xs text-red-500">{errorMessage}</p>
      )}

      {/* Dropdown panel — Fix 2: no mt-1, rounded-t-none, border-t-0 to merge with trigger */}
      {isOpen && (
        <div
          className={[
            inlineList ? "relative mt-2" : "absolute left-0 z-50",
            "w-full overflow-hidden",
            inlineList
              ? "rounded-lg border"
              : "rounded-t-none rounded-b-lg border border-t-0",
            dropdownBorderClass,
            "bg-white",
            "shadow-[0_4px_20px_rgba(0,0,0,0.08)]",
          ].join(" ")}
        >
          {isLoading ? (
            <div className="flex h-28 items-center justify-center">
              <span className="text-sm text-neutral-400">Loading...</span>
            </div>
          ) : filteredOptions.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 px-6 py-8 text-center">
              {noOptionsIcon ?? (
                <span className="mb-1 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-50">
                  <SearchIcon className="h-5 w-5 text-neutral-400" />
                </span>
              )}
              {noOptionsTitle && (
                <p className="text-[14px] font-semibold leading-[18px] text-[#1A1C1F]">
                  {noOptionsTitle}
                </p>
              )}
              <p className="max-w-[260px] text-[12px] leading-4 text-neutral-500">
                {noOptionsSubtitle ?? noOptionsText}
              </p>
            </div>
          ) : (
            <ul
              ref={listRef}
              role="listbox"
              aria-label={label}
              tabIndex={-1}
              onKeyDown={handleListKeyDown}
              className="max-h-56 overflow-y-auto py-1 outline-none"
            >
              {filteredOptions.map((option, index) => (
                <li
                  key={option.value}
                  role="option"
                  aria-selected={isOptionSelected(option)}
                  aria-disabled={option.disabled}
                  onClick={() => handleSelect(option)}
                  className={[
                    "flex cursor-pointer select-none items-center justify-between px-3 py-2.5 text-sm",
                    "transition-colors duration-100",
                    option.disabled ? "cursor-not-allowed opacity-40" : "",
                    "text-neutral-800",
                    !option.disabled
                      ? focusedIndex === index
                        ? "bg-neutral-50"
                        : "hover:bg-neutral-50"
                      : "",
                  ].join(" ")}
                >
                  <div className="flex min-w-0 flex-col gap-1">
                    <span className="truncate text-[14px] leading-[18px] text-[#1A1C1F]">
                      {option.primary ?? option.label}
                    </span>
                    {option.secondary && (
                      <span className="truncate text-[12px] leading-4 text-[rgba(98,98,98,0.69)]">
                        {option.secondary}
                      </span>
                    )}
                  </div>
                  {isOptionSelected(option) && (
                    <Check className="h-4 w-4 text-primary-300" />
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
});

export default AutocompleteComponent;
